import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import {
  DemographicDecision,
  DemographicField,
  DemographicProposal,
  demographicWarning,
  describeReferralFailure,
  undecidedFields,
} from '../../../../core/models/referral.model';
import { DemographicGroup, groupDemographicFields, reviewSummary, reviewValue } from '../../../../core/models/inbox-review';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { ReferralService } from '../../../../core/services/referral.service';
import { PatientAppService } from '../../../../core/services/patient-app.service';

export interface ReferralDemographicsDialogData {
  proposalId: string;
  groupId: string;
  patientName: string | null;
  /** 'patient-app': the patient changed their details in the patient app (tasks/patient-app.md §5.2); same review. */
  source?: 'referral' | 'patient-app';
}

/**
 * A referring practice changed a referred patient's details (referrals architecture §19, P10–P12, Phase 3): each changed
 * field with its value before, the proposed value, and this practice's current value. Every field this practice does
 * not already have needs accept or reject; accepted fields are written to this practice's patient. Nothing is applied
 * automatically.
 */
@Component({
  selector: 'app-referral-demographics-dialog',
  templateUrl: './referral-demographics-dialog.component.html',
  styleUrls: ['../inbox-review.scss'],
})
export class ReferralDemographicsDialogComponent extends BaseComponent implements OnInit {
  proposal: DemographicProposal | null = null;
  /** The fields under their section headings, built once when the proposal loads. */
  groups: DemographicGroup[] = [];
  decisions: Record<string, DemographicDecision | undefined> = {};
  isLoading = true;
  loadError: string | null = null;
  isSaving = false;
  actionError: string | null = null;
  submitAttempted = false;

  readonly value = reviewValue;
  readonly outcomeLabels: Record<string, string> = { accepted: 'Accepted', rejected: 'Rejected', same: 'Already the same' };

  constructor(
    private referralService: ReferralService,
    private patientAppService: PatientAppService,
    private dialogRef: MatDialogRef<ReferralDemographicsDialogComponent, { changed?: boolean }>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralDemographicsDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    const load$ = this.data.source === 'patient-app'
      ? this.patientAppService.getDetailProposal(this.data.proposalId, this.data.groupId)
      : this.referralService.getDemographicProposal(this.data.proposalId, this.data.groupId);
    load$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (proposal) => {
        this.proposal = proposal;
        this.groups = groupDemographicFields(proposal.fields);
        this.isLoading = false;
      },
      error: (error) => { this.isLoading = false; this.loadError = describeReferralFailure(error, 'This change could not be loaded.'); },
    });
  }

  get undecided(): string[] {
    return this.proposal ? undecidedFields(this.proposal.fields, this.decisions) : [];
  }

  /** "8 changes · 3 decided", with how many are already the same. */
  get summary(): string {
    if (!this.proposal) return '';
    const toDecide = this.proposal.fields.filter((f) => !f.alreadySame).length;
    return reviewSummary(toDecide, toDecide - this.undecided.length, this.proposal.fields.length - toDecide, 'change');
  }

  /** "In your record" is worth showing only when it is not what the change started from. */
  differsFromRecord(f: DemographicField): boolean {
    return reviewValue(f.current) !== reviewValue(f.old);
  }

  needsDecision(f: DemographicField): boolean {
    return !f.alreadySame && !this.decisions[f.field];
  }

  get warning(): string | null {
    return this.proposal ? demographicWarning(this.proposal.fields, this.decisions) : null;
  }

  setAll(decision: DemographicDecision): void {
    if (!this.proposal) return;
    for (const f of this.proposal.fields) if (!f.alreadySame) this.decisions[f.field] = decision;
  }

  save(): void {
    this.submitAttempted = true;
    if (!this.proposal || this.undecided.length || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    const decisions = Object.fromEntries(Object.entries(this.decisions).filter(([, d]) => !!d)) as Record<string, DemographicDecision>;
    const resolve$ = this.data.source === 'patient-app'
      ? this.patientAppService.resolveDetailProposal(this.data.proposalId, decisions, this.data.groupId)
      : this.referralService.resolveDemographicProposal(this.data.proposalId, decisions, this.data.groupId);
    resolve$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.isSaving = false;
        this.showToastMessage(result.applied.length ? `Updated ${result.applied.length} ${result.applied.length === 1 ? 'detail' : 'details'}.` : 'Changes reviewed; nothing updated.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The changes could not be saved. Please try again.');
      },
    });
  }

  close(): void {
    this.dialogRef.close({});
  }
}
